/** external */
import _ from "lodash";
import React, { useState } from "react";
import { connect } from "react-redux";
import { compose, graphql } from "lib/apolloClient";
import { Modal, Button, ToggleSwitch, Tooltip } from "@toddle-edu/ds-web";

/** internal */
import { useI18n } from "Hooks";
import { GRAPHQL_ONGOING_QUERY_NETWORK_STATUS } from "Constants";
import { FormTextInput } from "UIComponents/InputTextField";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import withLoader from "UIComponents/GqlWithLoader";
import {
  ORGANIZATION,
  ROLES_AND_PERMISSIONS,
} from "AddOnManager/routes/OrgAddOn/modules/OrgAddOnContants";
import { updateAddOnStatus } from "AddOnManager/routes/OrgAddOn/modules/OrgAddOnHelpers";
import { getInternalToolGenericDataQuery } from "InternalTools/modules/InternalToolsQuery";
import { getInternalToolGenericDataVariables } from "AddOnManager/modules/AddOnManagerUtils";
import { getRnPLevelsFeedQuery } from "AddOnManager/modules/AddOnManagerQueries";

/** relative */
import classes from "./RolesAndPermissions.scss";

const ROLES_AND_PERMISSIONS_CONFIGURATION_FEATURE_FLAG_ID =
  "165671393663391701";
const ROLES_AND_PERMISSIONS_CONSUMPTION_FEATURE_FLAG_ID = "165672034540459990";

const modalFooterStyle = {
  display: "flex",
  justifyContent: "flex-end",
};

const getAddOnPermissions = ({
  t,
  isConfigurationEnabled,
  isConsumptionEnabled,
  handleConfigurationToggle,
  handleConsumptionToggle,
  rnpViewData,
}) => {
  const ADD_ON_PERMISSIONS = [
    {
      key: ROLES_AND_PERMISSIONS_CONFIGURATION_FEATURE_FLAG_ID,
      label: t("acl:roles_and_permissions_configuration_add_on_title"),
      description: t(
        "acl:roles_and_permissions_configuration_add_on_description"
      ),
      checked: isConfigurationEnabled,
      onChange: handleConfigurationToggle,
      isDisabled: isConfigurationEnabled,
    },
    {
      key: ROLES_AND_PERMISSIONS_CONSUMPTION_FEATURE_FLAG_ID,
      label: t("acl:roles_and_permissions_consumption_add_on_title"),
      description: t(
        "acl:roles_and_permissions_consumption_add_on_description"
      ),
      checked: isConsumptionEnabled,
      onChange: handleConsumptionToggle,
      isDisabled:
        isConsumptionEnabled ||
        !isConfigurationEnabled ||
        _.isEmpty(rnpViewData),
    },
  ];
  return ADD_ON_PERMISSIONS;
};

const RolesAndPermissions = ({
  orgId,
  closeModal,
  updateAddOnStatus,
  internalToolGenericDataVariables,
  rnpLevelsFeedQueryVariables,
  isConfigurationEnabled,
  isConsumptionEnabled,
  onSuccessAddOnConfigChange = null,
  rnpViewData = [],
}) => {
  const { t } = useI18n();
  const [fullScreenLoader, setFullScreenLoader] = useState(false);

  const handleToggleChange = async ({ subType, currentValue }) => {
    setFullScreenLoader(true);

    await updateAddOnStatus({
      orgId,
      addOnType: ROLES_AND_PERMISSIONS,
      updateOn: ORGANIZATION,
      addOnSubType: subType,
      newStatus: _.toString(!currentValue),
      queryVariables: internalToolGenericDataVariables,
      additionalRefetchQueries: [
        {
          query: getRnPLevelsFeedQuery,
          variables: rnpLevelsFeedQueryVariables,
        },
      ],
    });
    if (!_.isNull(onSuccessAddOnConfigChange)) onSuccessAddOnConfigChange();

    setFullScreenLoader(false);
  };

  const handleConfigurationToggle = () =>
    handleToggleChange({
      subType: "CONFIGURATION",
      currentValue: isConfigurationEnabled,
    });

  const handleConsumptionToggle = () =>
    handleToggleChange({
      subType: "CONSUMPTION",
      currentValue: isConsumptionEnabled,
    });

  const addOnPermissions = getAddOnPermissions({
    t,
    isConfigurationEnabled,
    isConsumptionEnabled,
    handleConfigurationToggle,
    handleConsumptionToggle,
    rnpViewData,
  });

  const tooltipElement = (
    <Tooltip
      tooltip={
        "Roles and permissions has not been enabled successfully. Please reach out to tech"
      }
      showArrow={false}
    />
  );

  return (
    <Modal width="580px" height="600px" onClose={closeModal} dsVersion={"2.0"}>
      <Modal.Header title={t("common:roles_and_permissions")} isClosable />
      <Modal.Body isScrollable>
        <div className={classes.container}>
          <FormTextInput
            size="medium"
            label={t("common:org_id")}
            value={orgId}
            disabled={true}
            dsVersion={"2.0"}
          />
          {_.map(addOnPermissions, item => {
            const {
              key,
              label,
              description,
              checked,
              onChange,
              isDisabled,
            } = item;

            // If RnP Configuration is enabled but roles are not copied successfully, disable the consumption toggle
            const shouldTriggerTooltip =
              _.isEqual(
                key,
                ROLES_AND_PERMISSIONS_CONSUMPTION_FEATURE_FLAG_ID
              ) &&
              _.isEmpty(rnpViewData) &&
              isConfigurationEnabled;

            return (
              <div key={key} className={classes.switchContainer}>
                <div className={classes.labelContainer}>
                  <span className="text-label-l">{label}</span>
                  <span className="text-body-s">{description}</span>
                </div>
                <ConditionalWrapper
                  type="wrapperAsElement"
                  condition={shouldTriggerTooltip}
                  wrapper={tooltipElement}
                >
                  <ToggleSwitch
                    checked={checked}
                    onChange={onChange}
                    disabled={isDisabled}
                    dsVersion={"2.0"}
                  />
                </ConditionalWrapper>
              </div>
            );
          })}
        </div>
      </Modal.Body>
      <Modal.Footer style={modalFooterStyle}>
        <Button
          variant="neutral"
          type="outlined"
          size="medium"
          onClick={closeModal}
          dsVersion={"2.0"}
        >
          {t("common:close")}
        </Button>
      </Modal.Footer>
      {fullScreenLoader ? <FullScreenLoader /> : null}
    </Modal>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { orgId } = ownProps;

  const internalToolGenericDataVariables = getInternalToolGenericDataVariables({
    orgId,
    addOnType: ROLES_AND_PERMISSIONS,
  });

  const rnpLevelsFeedQueryVariables = {
    id: orgId,
    rnpViewFilters: {
      rnpLevels: [],
    },
  };

  return {
    internalToolGenericDataVariables,
    rnpLevelsFeedQueryVariables,
    isData: true,
    isLoading: false,
  };
};

const mapActionCreators = { updateAddOnStatus };

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getRnPLevelsFeedQuery, {
    skip: ({ orgId }) => _.isEmpty(orgId),
    name: "getRnPLevelsFeed",
    options: ({ rnpLevelsFeedQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: rnpLevelsFeedQueryVariables,
    }),
    props({ getRnPLevelsFeed, ownProps: { isData, isLoading } }) {
      const data = _.get(getRnPLevelsFeed, "node", {});

      const rnpViewData = _.get(data, "rnpView", {});

      return {
        isData: !_.isEmpty(data) && isData,
        isLoading: _.includes(
          GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
          getRnPLevelsFeed["networkStatus"] || isLoading
        ),
        rnpViewData,
      };
    },
  }),
  graphql(getInternalToolGenericDataQuery, {
    name: "getInternalToolGenericData",
    skip: ({ orgId }) => _.isEmpty(orgId),
    options: ({ internalToolGenericDataVariables }) => ({
      variables: internalToolGenericDataVariables,
      fetchPolicy: "cache-and-network",
    }),
    props: ({
      getInternalToolGenericData,
      ownProps: { isData, isLoading },
    }) => {
      const data = _.get(getInternalToolGenericData, "platform");

      const queryData = _.get(data, "internalToolsGenericQuery.data", []);

      const rolesAndPermissionsConfigurationObj = _.find(queryData, obj => {
        return (
          obj.fk_configuration_id ===
          ROLES_AND_PERMISSIONS_CONFIGURATION_FEATURE_FLAG_ID
        );
      });

      const rolesAndPermissionsConsumptionObj = _.find(queryData, obj => {
        return (
          obj.fk_configuration_id ===
          ROLES_AND_PERMISSIONS_CONSUMPTION_FEATURE_FLAG_ID
        );
      });

      const isConfigurationEnabled =
        _.get(
          _.head(_.get(rolesAndPermissionsConfigurationObj, "value")),
          "value"
        ) == "true" || false;

      const isConsumptionEnabled =
        _.get(
          _.head(_.get(rolesAndPermissionsConsumptionObj, "value")),
          "value"
        ) == "true" || false;

      return {
        isData: !_.isEmpty(data) && isData,
        isLoading:
          _.includes(
            GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
            getInternalToolGenericData.networkStatus
          ) || isLoading,
        isConfigurationEnabled,
        isConsumptionEnabled,
      };
    },
  }),
  withLoader
)(RolesAndPermissions);
